import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import api from "../lib/api";

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [demoRole, setDemoRole] = useState("student");

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    localStorage.removeItem("session_token");
    setUser(null);
  }, []);

  const value = useMemo(() => ({
    user, loading, setUser,
    role: user?.role || demoRole,
    demoRole, setDemoRole,
    authed: !!user,
    isDemo: !user,
    refresh: checkAuth,
    logout,
    // legacy compatibility with existing components
    setRole: setDemoRole,
    setAuthed: () => {},
  }), [user, loading, demoRole, checkAuth, logout]);

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthCtx);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
};

// Backward compat
export const useApp = useAuth;
export const AppProvider = AuthProvider;
